import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDoc, doc, updateDoc, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import BarcodeScanner from '../BarcodeScanner';
import { imprimirFactura, getFacturaHtml } from '../../utils/printDocument';
import { buscarProductosPorTexto } from '../../utils/buscarProductos';
import { sugerirMovimientoParaFactura, MOTIVO_LABEL } from '../../utils/conciliacionMatch';
import BancosView from './BancosView';
import DashboardFinancieroTab from './DashboardFinancieroTab';
import GastosView from './GastosView';
import Paginacion from '../common/Paginacion';

const POR_PAGINA = 30;

// Traduce el resultado de buscarProductosPorTexto (esquema StelOrder:
// name/reference/sales-price...) al vocabulario en español que usa este
// componente internamente — ver AlmacenView.js.
const mapProductoStel = (p) => ({
  ...p,
  nombre: p.name || '', referencia: p.reference || p['full-reference'] || '',
  marca: p.marca || '', ean: p.barcode || '', precioVenta: p['sales-price'] ?? 0,
});

// ── Utilidades básicas ────────────────────────────────────────────────────────

function Spinner({ sm }) {
  return (
    <div className={`${sm ? 'w-4 h-4 border-2' : 'w-8 h-8 border-2'} border-gray-200 border-t-red-500 rounded-full animate-spin`} />
  );
}

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-red-400 bg-white';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value, pad }) {
  return (
    <div className={`flex items-center justify-between ${pad ? 'px-4 py-2' : ''}`}>
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  );
}

const FORMA_PAGO = {
  53269: 'Domiciliación',
  92449: 'Garantía',
  11:    'Efectivo',
  1:     'Tarjeta',
  2:     'Transferencia',
  3:     'Bizum',
};

function formaPagoLabel(id) {
  return id ? (FORMA_PAGO[Number(id)] || `Opción ${id}`) : '—';
}

const FORMAS_PAGO_OPTIONS = [
  { value: '',      label: '— Sin especificar —' },
  { value: '53269', label: 'Domiciliación' },
  { value: '92449', label: 'Garantía' },
  { value: '11',    label: 'Efectivo' },
  { value: '1',     label: 'Tarjeta' },
  { value: '2',     label: 'Transferencia' },
  { value: '3',     label: 'Bizum' },
];

const ESTADO_CFG = {
  emitida: { label: 'Pendiente cobro', cls: 'bg-amber-100 text-amber-800' },
  cobrada: { label: 'Cobrada',         cls: 'bg-emerald-100 text-emerald-800' },
  abono:   { label: 'Abono',           cls: 'bg-purple-100 text-purple-800' },
};

// ── Utilidades de negocio ─────────────────────────────────────────────────────

function diasDesdeEmision(factura) {
  if (!factura.fecha) return null;
  const emision = new Date(factura.fecha);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return Math.floor((hoy - emision) / (1000 * 60 * 60 * 24));
}

function agingInfo(dias) {
  if (dias === null || dias === undefined) return { label: '—', cls: 'bg-gray-100 text-gray-500', urgent: false };
  if (dias <= 30) return { label: `${dias}d`, cls: 'bg-green-100 text-green-700', urgent: false };
  if (dias <= 60) return { label: `${dias}d`, cls: 'bg-amber-100 text-amber-700', urgent: false };
  if (dias <= 90) return { label: `${dias}d`, cls: 'bg-orange-100 text-orange-800', urgent: true };
  return { label: `${dias}d`, cls: 'bg-red-100 text-red-700', urgent: true };
}

function fechaVencimiento(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
}

function tipoFactura(factura) {
  if (factura.tipo === 'abono') return 'abono';
  const mp = Number(factura.metodoPago);
  if (mp === 92449) return 'garantia';
  if (mp === 53269) return 'mantenimiento';
  return 'cliente';
}

const TIPO_CFG = {
  cliente:       { label: 'Cliente',       cls: 'bg-blue-100 text-blue-700' },
  garantia:      { label: 'Garantía fab.', cls: 'bg-purple-100 text-purple-700' },
  mantenimiento: { label: 'Mantenimiento', cls: 'bg-teal-100 text-teal-700' },
  abono:         { label: 'Abono',         cls: 'bg-purple-100 text-purple-800' },
};

function exportarCSV(facturas) {
  const cols = ['Referencia','Fecha','Vencimiento','Cliente','Base (€)','IVA (€)','Total (€)','Estado','Forma Pago','Fecha Cobro','Tipo'];
  const rows = facturas.map(f => [
    f.referencia || '',
    f.fecha || '',
    fechaVencimiento(f.fecha) || '',
    f.clienteNombre || f.clienteId || '',
    f.importe ?? '',
    f.importeIva ?? '',
    f.importeTotal ?? f.importe ?? '',
    f.estado || '',
    formaPagoLabel(f.metodoPago),
    f.cobro?.fecha || '',
    TIPO_CFG[tipoFactura(f)]?.label || 'Cliente',
  ]);
  const csv = [cols, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facturas-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Tarjeta KPI ───────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color }) {
  const colors = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber:   'border-amber-200 bg-amber-50',
    blue:    'border-blue-200 bg-blue-50',
    red:     'border-red-200 bg-red-50',
    gray:    'border-gray-200 bg-gray-50',
  };
  return (
    <div className={`border rounded-2xl p-4 ${colors[color] || colors.gray}`}>
      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── AgingBadge ────────────────────────────────────────────────────────────────

function AgingBadge({ dias }) {
  const info = agingInfo(dias);
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${info.cls}`}>
      {info.label}
    </span>
  );
}

// ── QuickCobrarForm ───────────────────────────────────────────────────────────

function QuickCobrarForm({ factura, onSave, onClose }) {
  const hoy = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    fecha:       hoy,
    importe:     String(factura.importeTotal ?? factura.importe ?? ''),
    formaPagoId: factura.metodoPago ? String(factura.metodoPago) : '',
    reciboRef:   '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      const patch = {
        estado: 'cobrada',
        cobro: {
          fecha:       form.fecha || hoy,
          importe:     form.importe !== '' ? Number(form.importe) : null,
          formaPagoId: form.formaPagoId || null,
          reciboRef:   form.reciboRef || null,
          pagado:      true,
          liquidado:   false,
        },
      };
      await updateDoc(doc(db, 'facturas', factura.id), patch);
      onSave({ ...factura, ...patch });
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2" onClick={e => e.stopPropagation()}>
      <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Registrar cobro</p>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Field label="Fecha cobro">
          <input type="date" value={form.fecha}
            onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
            className={inputCls} />
        </Field>
        <Field label="Importe (€)">
          <input type="number" step="0.01" value={form.importe}
            onChange={e => setForm(p => ({ ...p, importe: e.target.value }))}
            className={inputCls} />
        </Field>
      </div>
      <Field label="Forma de pago">
        <select value={form.formaPagoId}
          onChange={e => setForm(p => ({ ...p, formaPagoId: e.target.value }))}
          className={inputCls}>
          {FORMAS_PAGO_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Ref. recibo (opcional)">
        <input value={form.reciboRef}
          onChange={e => setForm(p => ({ ...p, reciboRef: e.target.value }))}
          placeholder="Ej: RC-2025-001"
          className={inputCls} />
      </Field>
      <div className="flex gap-2 pt-1">
        <button onClick={onClose}
          className="flex-1 py-1.5 text-xs font-bold border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
          {saving ? <Spinner sm /> : 'Marcar cobrada'}
        </button>
      </div>
    </div>
  );
}

// ── Formulario de abono ───────────────────────────────────────────────────────

function AbonoForm({ factura, clienteNombre, onSave, onClose }) {
  const hoy          = new Date().toISOString().split('T')[0];
  const totalOrig    = Number(factura.importeTotal ?? factura.importe ?? 0);
  const [form, setForm] = useState({
    referencia:    `A-${factura.referencia}`,
    fecha:         hoy,
    totalDevolver: String(totalOrig.toFixed(2)),
    motivo:        '',
    parcial:       false,
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const totalNum = parseFloat(form.totalDevolver) || 0;
  const baseNum  = parseFloat((totalNum / 1.21).toFixed(2));
  const ivaNum   = parseFloat((totalNum - baseNum).toFixed(2));

  const handleSave = async () => {
    if (!form.referencia.trim()) { setErr('La referencia del abono es obligatoria'); return; }
    if (totalNum <= 0)           { setErr('El importe debe ser mayor que cero'); return; }
    setSaving(true); setErr('');
    try {
      const abonoDocRef = await addDoc(collection(db, 'facturas'), {
        tipo:             'abono',
        estado:           'abono',
        referencia:       form.referencia.trim(),
        facturaOrigenId:  factura.id,
        facturaOrigenRef: factura.referencia,
        fecha:            form.fecha || hoy,
        clienteNombre:    factura.clienteNombre || clienteNombre || null,
        clienteId:        factura.clienteId     || null,
        importe:          baseNum,
        importeIva:       ivaNum,
        importeTotal:     totalNum,
        motivo:           form.motivo || null,
        metodoPago:       factura.metodoPago     || null,
        origen:           'manual',
        ordenFirebaseId:  factura.ordenFirebaseId || null,
        syncedAt:         new Date().toISOString(),
      });

      await updateDoc(doc(db, 'facturas', factura.id), {
        abonoEmitido: true,
        abonoId:      abonoDocRef.id,
        abonoRef:     form.referencia.trim(),
      });

      onSave();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wider">Nueva factura de abono</p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs font-bold">✕</button>
      </div>
      <p className="text-xs text-purple-600 bg-purple-100 rounded-lg px-3 py-2">
        Genera una nota de crédito vinculada a <strong>{factura.referencia}</strong> para devolver el importe al cliente.
      </p>

      {err && <p className="text-xs text-red-600 font-semibold">{err}</p>}

      <Field label="Referencia del abono">
        <input value={form.referencia}
          onChange={e => setForm(p => ({ ...p, referencia: e.target.value }))}
          placeholder="Ej: A/2025/001"
          className={inputCls} />
      </Field>

      <Field label="Fecha del abono">
        <input type="date" value={form.fecha}
          onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
          className={inputCls} />
      </Field>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.parcial}
            onChange={e => setForm(p => ({
              ...p,
              parcial:       e.target.checked,
              totalDevolver: e.target.checked ? '' : String(totalOrig.toFixed(2)),
            }))}
            className="w-4 h-4 accent-purple-600" />
          <span className="text-xs font-semibold text-gray-700">Abono parcial</span>
        </label>
      </div>

      <Field label={`Total a devolver (€)${form.parcial ? '' : ` — máx. ${fmt(totalOrig)}`}`}>
        <input type="number" step="0.01" min="0.01" max={totalOrig} value={form.totalDevolver}
          onChange={e => setForm(p => ({ ...p, totalDevolver: e.target.value }))}
          className={inputCls} />
      </Field>

      {totalNum > 0 && (
        <div className="bg-white border border-purple-100 rounded-lg px-3 py-2 text-xs text-gray-600 space-y-0.5">
          <div className="flex justify-between"><span>Base imponible</span><span className="font-semibold">{fmt(baseNum)}</span></div>
          <div className="flex justify-between"><span>IVA 21%</span><span className="font-semibold">{fmt(ivaNum)}</span></div>
          <div className="flex justify-between font-bold text-gray-900 text-sm pt-1 border-t border-purple-100">
            <span>Total abono</span><span className="text-purple-700">-{fmt(totalNum)}</span>
          </div>
        </div>
      )}

      <Field label="Motivo del abono">
        <textarea value={form.motivo}
          onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
          placeholder="Ej: Servicio no realizado, error en facturación, devolución acordada…"
          rows={2}
          className={inputCls + ' resize-none'} />
      </Field>

      <div className="flex gap-2">
        <button onClick={onClose}
          className="flex-1 py-2 text-sm font-bold border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 py-2 text-sm font-bold bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50">
          {saving ? <Spinner sm /> : 'Emitir abono'}
        </button>
      </div>
    </div>
  );
}

// ── Editor de líneas de factura ───────────────────────────────────────────────

const LINE_ICONS_F = { desplazamiento: '🚗', mano_obra: '🔨', material: '📦' };

function LineItemRowF({ line, index, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-2.5">
      <span className="text-base flex-shrink-0">{LINE_ICONS_F[line.tipo] || '📦'}</span>
      <div className="flex-1 min-w-0 space-y-1">
        <input
          type="text" value={line.descripcion}
          onChange={e => onChange(index, 'descripcion', e.target.value)}
          placeholder="Descripción"
          className="w-full text-sm font-medium text-gray-800 bg-transparent border-none outline-none placeholder-gray-400"
        />
        {line.referencia && <p className="text-[10px] text-gray-400 font-mono">{line.referencia}</p>}
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400">Cant.</label>
          <input type="number" min="0.5" step="0.5" value={line.cantidad}
            onChange={e => onChange(index, 'cantidad', e.target.value)}
            className="w-12 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-1.5 py-1 text-center outline-none" />
          <label className="text-xs text-gray-400">×</label>
          <div className="relative flex-1">
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">€</span>
            <input type="number" min="0" step="0.5" value={line.precioUnitario}
              onChange={e => onChange(index, 'precioUnitario', e.target.value)}
              className="w-full pl-5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-1 outline-none" />
          </div>
          <span className="text-xs font-bold text-gray-800 flex-shrink-0 w-14 text-right">
            €{(Number(line.cantidad) * Number(line.precioUnitario)).toFixed(2)}
          </span>
        </div>
      </div>
      <button onClick={() => onRemove(index)} className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function LineasFacturaEditor({ lineasIniciales, onSave, onCancel }) {
  const [lines, setLines]             = useState([]);
  const [productos, setProductos]     = useState([]);
  const [busqMat, setBusqMat]         = useState('');
  const [showMatDrop, setShowMatDrop] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    const existing = (lineasIniciales || []).map(l => ({
      tipo:           l.tipo           || 'material',
      descripcion:    l.descripcion    || l.nombre || l['item-name'] || l.description || l.name || '',
      referencia:     l.referencia     || '',
      cantidad:       Number(l.cantidad || l.quantity || l.units || 1),
      precioUnitario: Number(l.precioUnitario || l.unitPrice || l['item-base-price'] || l.price || 0),
      productoId:     l.productoId     || null,
    }));
    setLines(existing);
  }, []); // only on mount

  // Búsqueda bajo demanda contra Firestore — nada de precargar el catálogo
  // completo (~22.700 productos reales de StelOrder), ver buscarProductos.js.
  useEffect(() => {
    if (busqMat.trim().length < 2) { setProductos([]); return; }
    let vivo = true;
    const t = setTimeout(() => {
      buscarProductosPorTexto(busqMat, { max: 6 }).then(rows => {
        if (vivo) setProductos(rows.map(mapProductoStel));
      }).catch(() => { if (vivo) setProductos([]); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busqMat]);

  const filteredProds = productos; // ya viene filtrado por buscarProductosPorTexto

  const handleBarcodeScan = async code => {
    setShowScanner(false);
    const rows = await buscarProductosPorTexto(code, { max: 6 }).catch(() => []);
    const found = rows.map(mapProductoStel).find(p => p.ean === code || p.referencia === code);
    if (found) addFromCatalog(found);
    else { setBusqMat(code); setShowMatDrop(true); }
  };

  const addFromCatalog = prod => {
    setLines(p => [...p, {
      tipo: 'material', descripcion: prod.nombre, referencia: prod.referencia || '',
      cantidad: 1, precioUnitario: prod.precioVenta || 0, productoId: prod.id,
    }]);
    setBusqMat(''); setShowMatDrop(false);
  };

  const addManual = () =>
    setLines(p => [...p, { tipo: 'material', descripcion: '', referencia: '', cantidad: 1, precioUnitario: 0, productoId: null }]);

  const addManoObra = () =>
    setLines(p => [...p, { tipo: 'mano_obra', descripcion: 'Mano de obra', cantidad: 1, precioUnitario: 45, productoId: null }]);

  const onChange = (i, field, value) =>
    setLines(p => p.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  const onRemove = i =>
    setLines(p => p.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + Number(l.cantidad) * Number(l.precioUnitario), 0);

  const handleSave = () => {
    const lineas = lines
      .filter(l => String(l.descripcion).trim())
      .map(l => ({ ...l, cantidad: Number(l.cantidad) || 1, precioUnitario: Number(l.precioUnitario) || 0 }));
    onSave(lineas, total);
  };

  return (
    <div className="border border-blue-200 rounded-xl overflow-hidden">
      <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-100 flex items-center justify-between">
        <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Líneas de factura</p>
        <button onClick={onCancel} className="text-xs text-gray-400 hover:text-gray-600 font-semibold">✕ Cancelar</button>
      </div>
      <div className="p-3 space-y-3 bg-gray-50">
        <div className="space-y-2">
          {lines.map((l, i) => (
            <LineItemRowF key={i} line={l} index={i} onChange={onChange} onRemove={onRemove} />
          ))}
          {lines.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">Sin líneas</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={addManoObra}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-gray-300">
            🔨 Mano de obra
          </button>
        </div>

        {showScanner && (
          <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} />
        )}

        <div className="relative">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
            <button onClick={() => setShowScanner(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-700" title="Escanear EAN">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </button>
            <input type="text" value={busqMat}
              onChange={e => { setBusqMat(e.target.value); setShowMatDrop(true); }}
              onFocus={() => setShowMatDrop(true)}
              placeholder="Buscar en catálogo o escanear EAN…"
              className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400" />
            <button onClick={addManual} className="text-xs text-gray-400 hover:text-gray-700 font-medium flex-shrink-0">+ manual</button>
          </div>

          {showMatDrop && busqMat.length > 1 && (
            <div className="absolute top-full left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-hidden">
              {filteredProds.length === 0 ? (
                <p className="text-sm text-gray-400 px-4 py-3">Sin resultados — usa "+ manual"</p>
              ) : filteredProds.map(p => (
                <button key={p.id} onClick={() => addFromCatalog(p)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-left border-b border-gray-50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.nombre}</p>
                    <p className="text-xs text-gray-400">{[p.referencia, p.marca].filter(Boolean).join(' · ')} · €{p.precioVenta || 0}</p>
                  </div>
                  <span className={`text-xs font-semibold flex-shrink-0 ${(p.stock ?? 0) <= (p.stockMinimo || 0) ? 'text-amber-600' : 'text-green-700'}`}>
                    {p.stock ?? '—'} ud.
                  </span>
                </button>
              ))}
              <button onClick={() => setShowMatDrop(false)} className="w-full text-xs text-gray-400 py-2 hover:bg-gray-50">Cerrar</button>
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <div className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-gray-100">
            <span className="text-sm font-semibold text-gray-600">Total líneas</span>
            <span className="text-base font-bold text-gray-900">€{total.toFixed(2)}</span>
          </div>
        )}

        <button onClick={handleSave}
          className="w-full py-2 text-xs font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700">
          Aplicar líneas
        </button>
      </div>
    </div>
  );
}

// ── Panel lateral de detalle ──────────────────────────────────────────────────

function FacturaPanel({ factura: inicial, onClose, onUpdated }) {
  const [factura, setFactura]         = useState(inicial);
  const [editing, setEditing]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState('');
  const [form, setForm]               = useState({});
  const [clienteNombre, setClienteNombre] = useState('');
  const [editingLineas, setEditingLineas] = useState(false);
  const [formLineas, setFormLineas]   = useState([]);
  const [showAbono, setShowAbono]     = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => { setFactura(inicial); }, [inicial]);

  useEffect(() => {
    if (factura?.clienteNombre) { setClienteNombre(factura.clienteNombre); return; }
    if (!factura?.ordenFirebaseId) return;
    getDoc(doc(db, 'ordenes', factura.ordenFirebaseId))
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setClienteNombre(d.clienteNombre || d.clientName || '');
        }
      })
      .catch(() => {});
  }, [factura?.ordenFirebaseId, factura?.clienteNombre]);

  const handleSendEmail = async () => {
    const email = factura.clienteEmail || prompt('Email del cliente:');
    if (!email) return;
    setSendingEmail(true);
    try {
      const html = await getFacturaHtml(factura, clienteNombre);
      const res  = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          to:      email,
          subject: `Factura ${factura.referencia}`,
          body:    html,
          senderName: 'Climatización Zapata',
        }),
      });
      const data = await res.json();
      setSaveMsg(data.success ? `✓ Email enviado a ${email}` : `Error: ${data.error}`);
    } catch (e) {
      setSaveMsg('Error al enviar el email: ' + e.message);
    } finally {
      setSendingEmail(false);
      setTimeout(() => setSaveMsg(''), 5000);
    }
  };

  const startEdit = () => {
    setForm({
      fecha:             factura.fecha         || '',
      clienteNombre:     factura.clienteNombre || clienteNombre || '',
      importe:           factura.importe       ?? '',
      importeIva:        factura.importeIva    ?? '',
      importeTotal:      factura.importeTotal  ?? '',
      tipoIva:           factura.tipoIva       ?? 21,
      metodoPago:        factura.metodoPago    ? String(factura.metodoPago) : '',
      notas:             factura.notas         || '',
      cobro_fecha:       factura.cobro?.fecha       || '',
      cobro_importe:     factura.cobro?.importe      ?? '',
      cobro_formaPagoId: factura.cobro?.formaPagoId  ? String(factura.cobro.formaPagoId) : '',
      cobro_reciboRef:   factura.cobro?.reciboRef    || '',
      cobro_liquidado:   factura.cobro?.liquidado    ?? false,
      tiene_cobro:       !!factura.cobro,
    });
    setFormLineas(factura.lineas || []);
    setEditingLineas(false);
    setEditing(true);
    setSaveMsg('');
  };

  const cancelEdit = () => { setEditing(false); setEditingLineas(false); setSaveMsg(''); };

  const handleSave = async () => {
    setSaving(true); setSaveMsg('');
    try {
      const patch = {
        fecha:         form.fecha         || null,
        clienteNombre: form.clienteNombre || null,
        importe:       form.importe !== '' ? Number(form.importe)       : null,
        importeIva:    form.importeIva !== '' ? Number(form.importeIva) : null,
        importeTotal:  form.importeTotal !== '' ? Number(form.importeTotal) : null,
        tipoIva:       Number(form.tipoIva) || 21,
        metodoPago:    form.metodoPago    || null,
        notas:         form.notas         || null,
        lineas:        formLineas,
      };

      if (form.tiene_cobro) {
        patch.cobro = {
          ...(factura.cobro || {}),
          fecha:       form.cobro_fecha       || null,
          importe:     form.cobro_importe !== '' ? Number(form.cobro_importe) : null,
          formaPagoId: form.cobro_formaPagoId || null,
          reciboRef:   form.cobro_reciboRef   || null,
          liquidado:   form.cobro_liquidado,
        };
        patch.estado = 'cobrada';
      } else if (factura.cobro) {
        patch.cobro  = null;
        patch.estado = 'emitida';
      }

      await updateDoc(doc(db, 'facturas', factura.id), patch);
      const updated = { ...factura, ...patch };
      setFactura(updated);
      onUpdated?.(updated);
      setEditing(false);
      setEditingLineas(false);
      setSaveMsg('Guardado correctamente');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      setSaveMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!factura) return null;

  const estadoCfg  = ESTADO_CFG[factura.estado] || { label: factura.estado, cls: 'bg-gray-100 text-gray-600' };
  const tipoCfg    = TIPO_CFG[tipoFactura(factura)];
  const cobro      = factura.cobro;
  const dias       = factura.estado === 'emitida' ? diasDesdeEmision(factura) : null;
  const venc       = fechaVencimiento(factura.fecha);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider font-bold">Factura</p>
            <p className="text-xl font-bold text-gray-900">{factura.referencia}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${estadoCfg.cls}`}>{estadoCfg.label}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tipoCfg.cls}`}>{tipoCfg.label}</span>
            {!editing && (
              <>
                <button
                  onClick={() => imprimirFactura(factura, clienteNombre)}
                  className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 text-sm"
                  title="Imprimir / PDF"
                >🖨️</button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-blue-50 text-gray-400 text-sm disabled:opacity-40"
                  title="Enviar por email al cliente"
                >{sendingEmail ? '⏳' : '📧'}</button>
                <button onClick={startEdit}
                  className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 text-sm"
                  title="Editar">✏️</button>
              </>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400">✕</button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4 space-y-4">
          {saveMsg && (
            <p className={`text-xs font-semibold px-3 py-2 rounded-xl ${saveMsg.startsWith('Error') ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
              {saveMsg}
            </p>
          )}

          {editing ? (
            <div className="space-y-4">
              {/* Datos */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-3">
                <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Datos de la factura</p>

                <Field label="Fecha emisión">
                  <input type="date" value={form.fecha}
                    onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
                    className={inputCls} />
                </Field>

                <Field label="Cliente">
                  <input value={form.clienteNombre}
                    onChange={e => setForm(p => ({ ...p, clienteNombre: e.target.value }))}
                    placeholder="Nombre del cliente"
                    className={inputCls} />
                </Field>

                <Field label="Tipo de IVA">
                  <select value={form.tipoIva}
                    onChange={e => {
                      const pct  = Number(e.target.value);
                      const base = parseFloat(form.importe) || 0;
                      const iva  = parseFloat((base * (pct / 100)).toFixed(2));
                      setForm(p => ({
                        ...p,
                        tipoIva:      pct,
                        importeIva:   String(iva),
                        importeTotal: String(parseFloat((base + iva).toFixed(2))),
                      }));
                    }}
                    className={inputCls}>
                    <option value={21}>21% — General</option>
                    <option value={10}>10% — Reducido</option>
                    <option value={0}>0% — Exento / Garantía</option>
                  </select>
                </Field>

                <div className="grid grid-cols-3 gap-2">
                  <Field label="Base (€)">
                    <input type="number" step="0.01" value={form.importe}
                      onChange={e => {
                        const base = parseFloat(e.target.value) || 0;
                        const pct  = Number(form.tipoIva) || 21;
                        const iva  = parseFloat((base * (pct / 100)).toFixed(2));
                        setForm(p => ({
                          ...p,
                          importe:      e.target.value,
                          importeIva:   String(iva),
                          importeTotal: String(parseFloat((base + iva).toFixed(2))),
                        }));
                      }}
                      className={inputCls} />
                  </Field>
                  <Field label={`IVA ${form.tipoIva ?? 21}% (€)`}>
                    <input type="number" step="0.01" value={form.importeIva}
                      onChange={e => setForm(p => ({ ...p, importeIva: e.target.value }))}
                      className={inputCls} />
                  </Field>
                  <Field label="Total (€)">
                    <input type="number" step="0.01" value={form.importeTotal}
                      onChange={e => setForm(p => ({ ...p, importeTotal: e.target.value }))}
                      className={inputCls} />
                  </Field>
                </div>

                <Field label="Forma de pago">
                  <select value={form.metodoPago}
                    onChange={e => setForm(p => ({ ...p, metodoPago: e.target.value }))}
                    className={inputCls}>
                    {FORMAS_PAGO_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Notas internas">
                  <textarea value={form.notas}
                    onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
                    placeholder="Observaciones, incidencias, acuerdos de pago…"
                    rows={2}
                    className={inputCls + ' resize-none'} />
                </Field>
              </div>

              {/* Cobro */}
              <div className={`border rounded-xl p-4 space-y-3 ${form.tiene_cobro ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-center justify-between">
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${form.tiene_cobro ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {form.tiene_cobro ? 'Cobro registrado' : 'Sin cobro'}
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.tiene_cobro}
                      onChange={e => setForm(p => ({ ...p, tiene_cobro: e.target.checked }))}
                      className="w-4 h-4 accent-emerald-600" />
                    <span className="text-xs font-semibold text-gray-700">Marcar como cobrada</span>
                  </label>
                </div>

                {form.tiene_cobro && (
                  <>
                    <Field label="Fecha cobro">
                      <input type="date" value={form.cobro_fecha}
                        onChange={e => setForm(p => ({ ...p, cobro_fecha: e.target.value }))}
                        className={inputCls} />
                    </Field>
                    <Field label="Importe cobrado (€)">
                      <input type="number" step="0.01" value={form.cobro_importe}
                        onChange={e => setForm(p => ({ ...p, cobro_importe: e.target.value }))}
                        className={inputCls} />
                    </Field>
                    <Field label="Forma de pago del cobro">
                      <select value={form.cobro_formaPagoId}
                        onChange={e => setForm(p => ({ ...p, cobro_formaPagoId: e.target.value }))}
                        className={inputCls}>
                        {FORMAS_PAGO_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Ref. recibo">
                      <input value={form.cobro_reciboRef}
                        onChange={e => setForm(p => ({ ...p, cobro_reciboRef: e.target.value }))}
                        placeholder="Ej: RC-2025-001"
                        className={inputCls} />
                    </Field>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.cobro_liquidado}
                        onChange={e => setForm(p => ({ ...p, cobro_liquidado: e.target.checked }))}
                        className="w-4 h-4 accent-emerald-600" />
                      <span className="text-xs font-semibold text-gray-700">Liquidado</span>
                    </label>
                  </>
                )}
              </div>

              {/* Líneas */}
              {editingLineas ? (
                <LineasFacturaEditor
                  lineasIniciales={formLineas}
                  onSave={(lineas, total) => {
                    setFormLineas(lineas);
                    if (lineas.length > 0) {
                      const base = lineas.reduce((s, l) => s + Number(l.cantidad) * Number(l.precioUnitario), 0);
                      const iva  = parseFloat((base * 0.21).toFixed(2));
                      setForm(p => ({
                        ...p,
                        importe:      String(parseFloat(base.toFixed(2))),
                        importeIva:   String(iva),
                        importeTotal: String(parseFloat((base + iva).toFixed(2))),
                      }));
                    }
                    setEditingLineas(false);
                  }}
                  onCancel={() => setEditingLineas(false)}
                />
              ) : (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Líneas {formLineas.length > 0 ? `(${formLineas.length})` : ''}
                    </p>
                    <button onClick={() => setEditingLineas(true)}
                      className="text-[10px] font-bold text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50">
                      ✏️ Editar líneas
                    </button>
                  </div>
                  {formLineas.length > 0 ? (
                    <div className="divide-y divide-gray-50">
                      {formLineas.map((l, i) => {
                        const nombre = l.descripcion || l.nombre || l['item-name'] || `Línea ${i + 1}`;
                        const cant   = Number(l.cantidad || 1);
                        const precio = Number(l.precioUnitario || l['item-base-price'] || 0);
                        return (
                          <div key={i} className="flex items-center gap-2 px-4 py-2 text-xs">
                            <span>{LINE_ICONS_F[l.tipo] || '📦'}</span>
                            <span className="flex-1 text-gray-700 truncate">{nombre}</span>
                            <span className="text-gray-500 font-semibold">{cant} × {precio.toFixed(2)} €</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 text-center py-3">Sin líneas — pulsa Editar líneas</p>
                  )}
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-2">
                <button onClick={cancelEdit}
                  className="flex-1 py-2.5 text-sm font-bold border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 py-2.5 text-sm font-bold bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50">
                  {saving ? <Spinner sm /> : 'Guardar cambios'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Vencimiento (solo si pendiente) */}
              {factura.estado === 'emitida' && dias !== null && (
                <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${
                  dias > 90 ? 'bg-red-50 border border-red-200' :
                  dias > 60 ? 'bg-orange-50 border border-orange-200' :
                  dias > 30 ? 'bg-amber-50 border border-amber-200' :
                  'bg-green-50 border border-green-200'
                }`}>
                  <div>
                    <p className="text-xs font-bold text-gray-700">Vence el {fmtDate(venc)}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Emitida hace {dias} días</p>
                  </div>
                  <AgingBadge dias={dias} />
                </div>
              )}

              {/* Datos principales */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <Row label="Fecha emisión" value={fmtDate(factura.fecha)} />
                {(factura.clienteNombre || clienteNombre) && (
                  <Row label="Cliente" value={factura.clienteNombre || clienteNombre} />
                )}
                {factura.clienteId && <Row label="ID cliente" value={factura.clienteId} />}
                {factura.ordenFirebaseId && <Row label="Ref. orden" value={factura.ordenFirebaseId} />}
              </div>

              {/* Importes */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Importes</p>
                </div>
                <div className="divide-y divide-gray-50">
                  <Row label="Base imponible" value={fmt(factura.importe)} pad />
                  <Row label={`IVA (${factura.tipoIva ?? 21}%)`} value={fmt(factura.importeIva)} pad />
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                    <span className="text-sm font-bold text-gray-700">Total</span>
                    <span className="text-base font-bold text-gray-900">{fmt(factura.importeTotal ?? factura.importe)}</span>
                  </div>
                </div>
              </div>

              {factura.metodoPago && (
                <div className="bg-gray-50 rounded-xl p-4 text-sm">
                  <Row label="Forma de pago" value={formaPagoLabel(factura.metodoPago)} />
                </div>
              )}

              {cobro ? (
                <div className="border border-emerald-100 rounded-xl overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2.5 border-b border-emerald-100">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Cobro registrado</p>
                  </div>
                  <div className="p-4 space-y-1.5 text-sm">
                    <Row label="Fecha cobro"   value={fmtDate(cobro.fecha)} />
                    <Row label="Importe"       value={fmt(cobro.importe)} />
                    <Row label="Forma de pago" value={formaPagoLabel(cobro.formaPagoId)} />
                    {cobro.reciboRef && <Row label="Ref. recibo"  value={cobro.reciboRef} />}
                    {cobro.reciboId  && <Row label="ID recibo"    value={cobro.reciboId} />}
                    <Row label="Liquidado" value={cobro.liquidado ? 'Sí' : 'No'} />
                  </div>
                </div>
              ) : (
                <div className="border border-amber-100 rounded-xl px-4 py-3 bg-amber-50 text-sm text-amber-800 text-center font-semibold">
                  Pendiente de cobro
                </div>
              )}

              {/* Abono */}
              {factura.estado === 'cobrada' && !factura.abonoEmitido && !showAbono && (
                <button
                  onClick={() => setShowAbono(true)}
                  className="w-full py-2.5 text-sm font-bold border-2 border-dashed border-purple-300 text-purple-600 rounded-xl hover:bg-purple-50 transition-colors"
                >
                  ↩ Emitir factura de abono / devolución
                </button>
              )}

              {factura.abonoEmitido && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <span className="text-purple-500 text-lg">↩</span>
                  <div>
                    <p className="text-xs font-bold text-purple-700">Abono emitido</p>
                    <p className="text-[10px] text-purple-500">{factura.abonoRef || factura.abonoId}</p>
                  </div>
                </div>
              )}

              {showAbono && (
                <AbonoForm
                  factura={factura}
                  clienteNombre={clienteNombre}
                  onSave={() => {
                    setShowAbono(false);
                    const updated = { ...factura, abonoEmitido: true };
                    setFactura(updated);
                    onUpdated?.(updated);
                  }}
                  onClose={() => setShowAbono(false)}
                />
              )}

              {/* Notas internas */}
              {factura.notas && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-yellow-700 uppercase tracking-wider mb-1.5">Notas internas</p>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{factura.notas}</p>
                </div>
              )}

              {/* Líneas */}
              {Array.isArray(factura.lineas) && factura.lineas.length > 0 && (
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Líneas ({factura.lineas.length})</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {factura.lineas.map((l, i) => {
                      const nombre = l.descripcion || l.nombre || l['item-name'] || l.description || l.name || `Línea ${i + 1}`;
                      const cant   = Number(l.cantidad || l.units || 1);
                      const precio = Number(l.precioUnitario || l['item-base-price'] || l.price || 0);
                      return (
                        <div key={i} className="flex items-center gap-2 px-4 py-2 text-xs">
                          <span className="flex-shrink-0">{LINE_ICONS_F[l.tipo] || '📦'}</span>
                          <span className="text-gray-700 flex-1 truncate">{nombre}</span>
                          {l.referencia && <span className="text-gray-400 font-mono">{l.referencia}</span>}
                          <span className="text-gray-500 font-semibold flex-shrink-0">{cant} × {precio.toFixed(2)} €</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-[10px] text-gray-300 text-center pb-4">
                Origen: {factura.origen || 'stelorder'} · Sync: {factura.syncedAt?.split('T')[0] || '—'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab Resumen (dashboard) ───────────────────────────────────────────────────

function ResumenTab({ facturas }) {
  const hoy     = new Date();
  const mesStr  = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  const anoStr  = String(hoy.getFullYear());

  const cobradas  = facturas.filter(f => f.estado === 'cobrada');
  const emitidas  = facturas.filter(f => f.estado === 'emitida');
  const abonos    = facturas.filter(f => f.tipo === 'abono');

  const abonosMes = abonos.filter(f => (f.fecha || '').startsWith(mesStr))
    .reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);
  const abonosAno = abonos.filter(f => (f.fecha || '').startsWith(anoStr))
    .reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);

  const cobMes = cobradas
    .filter(f => (f.fecha || '').startsWith(mesStr))
    .reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0) - abonosMes;

  const cobAno = cobradas
    .filter(f => (f.fecha || '').startsWith(anoStr))
    .reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0) - abonosAno;

  const totalPend = emitidas.reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);

  const vencidas = emitidas.filter(f => {
    const d = diasDesdeEmision(f);
    return d !== null && d > 30;
  });
  const totalVenc = vencidas.reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);

  // Aging buckets (solo emitidas)
  const buckets = [
    { label: '≤ 30 días', min: 0,  max: 30,  cls: 'bg-green-50 border-green-200 text-green-800' },
    { label: '31–60 días', min: 31, max: 60,  cls: 'bg-amber-50 border-amber-200 text-amber-800' },
    { label: '61–90 días', min: 61, max: 90,  cls: 'bg-orange-50 border-orange-200 text-orange-800' },
    { label: '> 90 días',  min: 91, max: 9999, cls: 'bg-red-50 border-red-200 text-red-800' },
  ].map(b => {
    const list = emitidas.filter(f => {
      const d = diasDesdeEmision(f);
      return d !== null && d >= b.min && d <= b.max;
    });
    return { ...b, count: list.length, total: list.reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0) };
  });

  // IVA trimestral — facturas del año actual, sin garantías
  // Abonos restan como facturas negativas en el mismo trimestre
  const quarters = [1, 2, 3, 4].map(q => {
    const meses = [(q - 1) * 3 + 1, (q - 1) * 3 + 2, (q - 1) * 3 + 3]
      .map(m => `${anoStr}-${String(m).padStart(2, '0')}`);
    const list = facturas.filter(f =>
      meses.some(m => (f.fecha || '').startsWith(m)) &&
      tipoFactura(f) !== 'garantia'
    );
    const sign = f => f.tipo === 'abono' ? -1 : 1;
    const base  = list.reduce((s, f) => s + sign(f) * Number(f.importe ?? 0), 0);
    const iva   = list.reduce((s, f) => s + sign(f) * Number(f.importeIva ?? (Number(f.importe ?? 0) * 0.21)), 0);
    const total = list.reduce((s, f) => s + sign(f) * Number(f.importeTotal ?? f.importe ?? 0), 0);
    return { q, base, iva, total, count: list.length };
  });

  const ultimasPend = [...emitidas]
    .sort((a, b) => diasDesdeEmision(b) - diasDesdeEmision(a))
    .slice(0, 5);

  return (
    <div className="p-4 space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Cobrado este mes" value={fmt(cobMes)} sub={mesStr} color="emerald" />
        <KpiCard label={`Cobrado ${anoStr}`} value={fmt(cobAno)} sub={`${cobradas.filter(f => (f.fecha || '').startsWith(anoStr)).length} facturas`} color="blue" />
        <KpiCard label="Pendiente de cobro" value={fmt(totalPend)} sub={`${emitidas.length} facturas`} color={emitidas.length > 0 ? 'amber' : 'gray'} />
        <KpiCard label="Vencidas (>30d)" value={fmt(totalVenc)} sub={`${vencidas.length} facturas`} color={vencidas.length > 0 ? 'red' : 'gray'} />
      </div>

      {/* Aging */}
      {emitidas.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Antigüedad — pendiente de cobro</p>
          <div className="grid grid-cols-2 gap-2">
            {buckets.map(b => (
              <div key={b.label} className={`border rounded-xl p-3 ${b.cls}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1">{b.label}</p>
                <p className="text-base font-bold">{b.count} fact.</p>
                <p className="text-xs font-semibold">{fmt(b.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Abonos emitidos */}
      {abonos.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Abonos / devoluciones</p>
          <div className="border border-purple-100 rounded-xl overflow-hidden">
            <div className="bg-purple-50 px-4 py-2.5 flex items-center justify-between">
              <span className="text-xs font-bold text-purple-700">{abonos.length} abono{abonos.length !== 1 ? 's' : ''} emitidos en {anoStr}</span>
              <span className="text-sm font-bold text-purple-800">-{fmt(abonosAno)}</span>
            </div>
            <div className="divide-y divide-purple-50">
              {abonos.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-xs">
                  <span className="text-purple-400">↩</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-800">{a.referencia}</p>
                    {a.facturaOrigenRef && (
                      <p className="text-gray-400">Abono de {a.facturaOrigenRef}</p>
                    )}
                    {a.motivo && <p className="text-gray-400 truncate italic">{a.motivo}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-purple-700">-{fmt(a.importeTotal ?? a.importe)}</p>
                    <p className="text-gray-400">{fmtDate(a.fecha)}</p>
                  </div>
                </div>
              ))}
              {abonos.length > 5 && (
                <p className="text-xs text-gray-400 px-4 py-2">y {abonos.length - 5} más…</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* IVA trimestral */}
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">IVA trimestral {anoStr} — Modelo 303</p>
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            <div className="px-3 py-2">Trim.</div>
            <div className="px-3 py-2 text-right">Base</div>
            <div className="px-3 py-2 text-right">IVA 21%</div>
            <div className="px-3 py-2 text-right">Total</div>
          </div>
          {quarters.map(q => (
            <div key={q.q} className="grid grid-cols-4 border-b border-gray-50 last:border-0 text-xs">
              <div className="px-3 py-2.5 font-bold text-gray-700">T{q.q}</div>
              <div className="px-3 py-2.5 text-right text-gray-600">{fmt(q.base)}</div>
              <div className="px-3 py-2.5 text-right text-blue-700 font-semibold">{fmt(q.iva)}</div>
              <div className="px-3 py-2.5 text-right font-bold text-gray-900">{fmt(q.total)}</div>
            </div>
          ))}
          <div className="grid grid-cols-4 bg-gray-50 text-xs font-bold border-t border-gray-200">
            <div className="px-3 py-2.5 text-gray-700">Total</div>
            <div className="px-3 py-2.5 text-right text-gray-700">{fmt(quarters.reduce((s, q) => s + q.base, 0))}</div>
            <div className="px-3 py-2.5 text-right text-blue-700">{fmt(quarters.reduce((s, q) => s + q.iva, 0))}</div>
            <div className="px-3 py-2.5 text-right text-gray-900">{fmt(quarters.reduce((s, q) => s + q.total, 0))}</div>
          </div>
        </div>
        <p className="text-[9px] text-gray-400 mt-1">* Excluye garantías de fabricante. Incluye emitidas y cobradas.</p>
      </div>

      {/* Más urgentes pendientes */}
      {ultimasPend.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Más urgentes — pendiente de cobro</p>
          <div className="space-y-1">
            {ultimasPend.map(f => {
              const dias = diasDesdeEmision(f);
              return (
                <div key={f.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-3 py-2.5">
                  <AgingBadge dias={dias} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{f.referencia}</p>
                    <p className="text-xs text-gray-400 truncate">{f.clienteNombre || f.clienteId || '—'}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900 flex-shrink-0">{fmt(f.importeTotal ?? f.importe)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

// ── Sugerencia de conciliación (pestaña Pendiente) ──────────────────────────────
// Bandeja de "posible pago" sin salir de Facturas — antes solo se podía
// conciliar yendo a Bancos y buscando la factura a mano desde el movimiento.
function SugerenciaConciliacion({ factura, movimientos, conciliando, onConciliar }) {
  const sugerencia = useMemo(() => sugerirMovimientoParaFactura(factura, movimientos), [factura, movimientos]);
  if (!sugerencia) return null;
  const { movimiento, motivo, exacto } = sugerencia;
  return (
    <button
      onClick={e => { e.stopPropagation(); onConciliar(movimiento); }}
      disabled={conciliando}
      title={`Movimiento del ${fmtDate(movimiento.fecha)}: "${movimiento.concepto}"`}
      className="text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 transition-colors disabled:opacity-50"
    >
      {conciliando ? 'Conciliando…' : `💡 Posible pago (${fmt(Math.abs(movimiento.importe))}${exacto ? ', importe exacto' : ''}) · por ${MOTIVO_LABEL[motivo]}`}
    </button>
  );
}

export default function FacturasView({ initialTab } = {}) {
  const [facturas, setFacturas]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadingExtra, setLoadingExtra] = useState(false); // sigue cargando lotes en segundo plano
  const [tab, setTab]                   = useState(initialTab || 'resumen');
  const [busqueda, setBusqueda]         = useState('');
  const [mesFiltro, setMesFiltro]       = useState('');
  const [tipoFiltro, setTipoFiltro]     = useState('');
  const [formaPagoFiltro, setFormaPagoFiltro] = useState('');
  const [pagina, setPagina]             = useState(1);
  const [seleccionada, setSeleccionada] = useState(null);
  const [syncing, setSyncing]           = useState(false);
  const [syncMsg, setSyncMsg]           = useState('');
  const [quickCobrarId, setQuickCobrarId] = useState(null);
  const [movsSinConciliar, setMovsSinConciliar] = useState([]);
  const [conciliandoId, setConciliandoId] = useState(null);
  // 'esteAno' (por defecto) o 'todo' — cargar el histórico completo entero
  // en el día a día no tenía sentido (más lento y no hace falta la mayoría
  // de las veces). "Ver años anteriores" cambia a 'todo' y recarga.
  const [alcance, setAlcance] = useState('esteAno');

  // Antes: onSnapshot con limit(500) — cómodo por el tiempo real, pero con
  // más de 500 facturas (cualquier empresa con unos años de histórico) las
  // más antiguas simplemente no existían para esta pantalla, ni en "Todas"
  // ni en los KPIs de "Resumen" (que se calculan sobre este mismo estado).
  // Por defecto ahora se limita a facturas de este año (rápido, sin lotes);
  // "Ver años anteriores" carga TODO en lotes de 500 por cursor (mismo
  // patrón que ClientesView.js) — el primer lote se pinta al momento, el
  // resto sigue en segundo plano. Se pierde el auto-refresh en tiempo real
  // de onSnapshot a cambio; se recarga a mano tras sincronizar o conciliar.
  const cargarFacturas = useCallback(async (alcanceActual) => {
    setLoading(true); setLoadingExtra(false);
    try {
      const soloEsteAno = alcanceActual === 'esteAno';
      const inicioAno = `${new Date().getFullYear()}-01-01`;
      const BATCH = 500;
      let all = [];
      let cursor = null;
      let first = true;
      while (true) {
        const base = soloEsteAno
          ? [collection(db, 'facturas'), where('fecha', '>=', inicioAno), orderBy('fecha', 'desc')]
          : [collection(db, 'facturas'), orderBy('fecha', 'desc')];
        const q = cursor
          ? query(...base, startAfter(cursor), limit(BATCH))
          : query(...base, limit(BATCH));
        const snap = await getDocs(q);
        if (snap.empty) break;
        all = all.concat(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (first) {
          setFacturas([...all]);
          setLoading(false);
          first = false;
          if (snap.docs.length === BATCH) setLoadingExtra(true);
        } else {
          setFacturas([...all]);
        }
        cursor = snap.docs[snap.docs.length - 1];
        if (snap.docs.length < BATCH) break;
      }
    } catch (e) {
      console.error('[FacturasView] error cargando facturas:', e.message);
    } finally {
      setLoading(false);
      setLoadingExtra(false);
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      // syncFacturas.js tiene su propio cron (netlify.toml) — Netlify
      // bloquea a nivel de edge cualquier POST directo del navegador a una
      // función programada, así que el botón pasa por este wrapper (mismo
      // criterio que forzarSyncDeltaStelOrder.js en Migración).
      const res  = await fetch('/.netlify/functions/forzarSyncFacturas', { method: 'POST', headers: { ...authService.authHeader() } });
      const data = await res.json();
      if (data.success) {
        setSyncMsg(`✓ ${data.facturas} facturas, ${data.recibos} recibos sincronizados`);
        cargarFacturas(alcance);
      } else {
        setSyncMsg(`Error: ${data.error || 'desconocido'}`);
      }
    } catch (e) {
      setSyncMsg(`Error: ${e.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(''), 6000);
    }
  };

  useEffect(() => { cargarFacturas(alcance); }, [cargarFacturas, alcance]);

  // Movimientos bancarios sin conciliar — para poder sugerir "posible pago"
  // desde la propia pestaña Pendiente, sin tener que ir a Bancos a buscarlo
  // a mano. Solo los no conciliados (el resto no aporta nada a la sugerencia)
  // y solo una vez, no en tiempo real — no hace falta más precisión aquí.
  useEffect(() => {
    getDocs(query(collection(db, 'movimientos_banco'), orderBy('fecha', 'desc'), limit(500)))
      .then(snap => setMovsSinConciliar(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(m => !m.conciliado)))
      .catch(() => setMovsSinConciliar([]));
  }, []);

  const handleConciliarDesdeFacturas = async (factura, movimiento) => {
    setConciliandoId(factura.id);
    try {
      const resp = await fetch('/.netlify/functions/conciliarPago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ movimientoId: movimiento.id, facturaId: factura.id }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setMovsSinConciliar(prev => prev.filter(m => m.id !== movimiento.id));
      // Ya no hay onSnapshot que lo traiga solo — se parchea en local en vez
      // de recargar toda la lista por una sola factura.
      setFacturas(prev => prev.map(f => f.id === factura.id
        ? { ...f, estado: 'cobrada', conciliada: true, cobro: { fecha: movimiento.fecha, importe: Math.abs(movimiento.importe), pagado: true, liquidado: true } }
        : f));
    } catch (e) {
      setSyncMsg(`Error al conciliar: ${e.message}`);
      setTimeout(() => setSyncMsg(''), 6000);
    } finally {
      setConciliandoId(null);
    }
  };

  const hoy = new Date();
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;

  const kpis = useMemo(() => {
    const cobradas = facturas.filter(f => f.estado === 'cobrada');
    const emitidas = facturas.filter(f => f.estado === 'emitida');
    const totalCobMes = cobradas
      .filter(f => (f.fecha || '').startsWith(mesActual))
      .reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);
    const totalPend = emitidas.reduce((s, f) => s + Number(f.importeTotal ?? f.importe ?? 0), 0);
    const vencidasCount = emitidas.filter(f => { const d = diasDesdeEmision(f); return d !== null && d > 30; }).length;
    return { cobradas: cobradas.length, emitidas: emitidas.length, totalCobMes, totalPend, vencidasCount };
  }, [facturas, mesActual]);

  const listaFiltrada = useMemo(() => {
    return facturas.filter(f => {
      if (tab === 'pendiente' && f.estado !== 'emitida')  return false;
      if (tab === 'cobradas'  && f.estado !== 'cobrada')  return false;
      if (mesFiltro && !(f.fecha || '').startsWith(mesFiltro)) return false;
      if (tipoFiltro && tipoFactura(f) !== tipoFiltro) return false;
      if (formaPagoFiltro && String(f.metodoPago) !== formaPagoFiltro) return false;
      if (busqueda) {
        const q2 = busqueda.toLowerCase();
        if (!(f.referencia      || '').toLowerCase().includes(q2) &&
            !(f.clienteNombre   || '').toLowerCase().includes(q2) &&
            !(f.clienteId       || '').toLowerCase().includes(q2)) return false;
      }
      return true;
    }).sort((a, b) => {
      // Pendiente: más antiguas primero (más urgentes)
      if (tab === 'pendiente') {
        const da = diasDesdeEmision(a) ?? 0;
        const db2 = diasDesdeEmision(b) ?? 0;
        return db2 - da;
      }
      return 0;
    });
  }, [facturas, tab, busqueda, mesFiltro, tipoFiltro, formaPagoFiltro]);

  // Volver a página 1 cuando cambia la pestaña o cualquier filtro — si no,
  // al filtrar podías quedarte en una página que ya no existe.
  useEffect(() => { setPagina(1); }, [tab, busqueda, mesFiltro, tipoFiltro, formaPagoFiltro]);

  const totalPaginas      = Math.max(1, Math.ceil(listaFiltrada.length / POR_PAGINA));
  const paginaReal        = Math.min(pagina, totalPaginas);
  const listaPaginada     = listaFiltrada.slice((paginaReal - 1) * POR_PAGINA, paginaReal * POR_PAGINA);
  const desde             = listaFiltrada.length === 0 ? 0 : (paginaReal - 1) * POR_PAGINA + 1;
  const hasta             = Math.min(paginaReal * POR_PAGINA, listaFiltrada.length);

  const TABS = [
    { key: 'resumen',   label: 'Resumen' },
    { key: 'pendiente', label: 'Pendiente', count: kpis.emitidas, alert: kpis.vencidasCount > 0 },
    { key: 'cobradas',  label: 'Cobradas',  count: kpis.cobradas },
    { key: 'todas',     label: 'Todas',     count: facturas.length },
    { key: 'bancos',    label: 'Bancos' },
    { key: 'analitica', label: 'Analítica' },
    { key: 'gastos',    label: 'Gastos' },
  ];
  // "Bancos", "Analítica" y "Gastos" traen su propia interfaz completa
  // (BancosView.js ya tiene sus propias pestañas/dashboard; la Analítica
  // financiera es un informe agregado en servidor; Gastos es la bandeja de
  // facturas de proveedor) — no aplican los filtros de fecha/tipo/forma de
  // pago ni la lista de facturas, pensados para las otras 4 pestañas.
  const esVistaEspecial = tab === 'bancos' || tab === 'analitica' || tab === 'gastos';

  const hasFilters = busqueda || mesFiltro || tipoFiltro || formaPagoFiltro;
  const showFilters = tab !== 'resumen';

  return (
    <div className="space-y-4 pb-12">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1">
          {syncMsg && (
            <p className={`text-xs font-semibold ${syncMsg.startsWith('Error') ? 'text-red-600' : 'text-emerald-600'}`}>
              {syncMsg}
            </p>
          )}
          {!syncMsg && loadingExtra && (
            <p className="text-xs text-gray-400">Cargando más facturas en segundo plano… ({facturas.length} hasta ahora)</p>
          )}
        </div>
        {!esVistaEspecial && (
          alcance === 'esteAno' ? (
            <button
              onClick={() => setAlcance('todo')}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Ver años anteriores
            </button>
          ) : (
            <button
              onClick={() => setAlcance('esteAno')}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Solo este año
            </button>
          )
        )}
        {tab === 'todas' && (
          <button
            onClick={() => exportarCSV(listaFiltrada)}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 transition-colors"
          >
            ↓ CSV
          </button>
        )}
        {!esVistaEspecial && (
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {syncing ? <Spinner sm /> : '↻'}
          {syncing ? 'Sincronizando…' : 'Sync StelOrder'}
        </button>
        )}
      </div>

      {/* Panel principal */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-shrink-0 flex-1 min-w-0 py-3 px-2 text-xs font-bold transition-colors relative ${
                tab === t.key
                  ? 'text-red-600 border-b-2 border-red-500 bg-red-50'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {t.label}
              {t.count != null && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  tab === t.key
                    ? 'bg-red-100 text-red-600'
                    : t.alert ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                }`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'resumen' ? (
          loading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : (
            <ResumenTab facturas={facturas} />
          )
        ) : tab === 'bancos' ? (
          <div className="p-4">
            <BancosView />
          </div>
        ) : tab === 'analitica' ? (
          <div className="p-4">
            <DashboardFinancieroTab />
          </div>
        ) : tab === 'gastos' ? (
          <div className="p-4">
            <GastosView />
          </div>
        ) : (
          <>
            {/* Filtros */}
            {showFilters && (
              <div className="p-3 border-b border-gray-50 space-y-2">
                <div className="flex gap-2">
                  <input
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    placeholder="Buscar referencia, cliente…"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                  <input
                    type="month"
                    value={mesFiltro}
                    onChange={e => setMesFiltro(e.target.value)}
                    className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                </div>
                {tab === 'todas' && (
                  <div className="flex gap-2">
                    <select value={tipoFiltro}
                      onChange={e => setTipoFiltro(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white">
                      <option value="">Todos los tipos</option>
                      <option value="cliente">Cliente</option>
                      <option value="garantia">Garantía fab.</option>
                      <option value="mantenimiento">Mantenimiento</option>
                      <option value="abono">Abono / devolución</option>
                    </select>
                    <select value={formaPagoFiltro}
                      onChange={e => setFormaPagoFiltro(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white">
                      <option value="">Todas las formas de pago</option>
                      {FORMAS_PAGO_OPTIONS.filter(o => o.value).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {hasFilters && (
                  <button
                    onClick={() => { setBusqueda(''); setMesFiltro(''); setTipoFiltro(''); setFormaPagoFiltro(''); }}
                    className="text-xs text-gray-400 hover:text-gray-600 font-semibold"
                  >Limpiar filtros</button>
                )}
              </div>
            )}

            {/* Lista */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Spinner /></div>
            ) : listaFiltrada.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-3xl mb-2">🧾</p>
                <p className="text-sm font-semibold">Sin facturas</p>
                <p className="text-xs mt-1">Prueba a cambiar los filtros o sincronizar StelOrder</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {listaPaginada.map(f => {
                  const estadoCfg   = ESTADO_CFG[f.estado] || { label: f.estado, cls: 'bg-gray-100 text-gray-600' };
                  const tipoCfg2    = TIPO_CFG[tipoFactura(f)];
                  const importe     = f.importeTotal ?? f.importe;
                  const cobro       = f.cobro;
                  const dias        = f.estado === 'emitida' ? diasDesdeEmision(f) : null;
                  const isQuick     = quickCobrarId === f.id;
                  const esAbono     = f.tipo === 'abono';

                  return (
                    <div key={f.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                      <button
                        className="w-full text-left"
                        onClick={() => { setSeleccionada(f); setQuickCobrarId(null); }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                              <p className="text-sm font-bold text-gray-900">{f.referencia}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${estadoCfg.cls}`}>
                                {estadoCfg.label}
                              </span>
                              {tab === 'todas' && (
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tipoCfg2.cls}`}>
                                  {tipoCfg2.label}
                                </span>
                              )}
                              {dias !== null && <AgingBadge dias={dias} />}
                            </div>
                            <p className="text-xs text-gray-400">
                              {f.clienteNombre || (f.clienteId ? `Cliente ${f.clienteId}` : '—')}
                              {' · '}{fmtDate(f.fecha)}
                              {esAbono && f.facturaOrigenRef ? ` · Abono de ${f.facturaOrigenRef}` : ''}
                              {!esAbono && cobro?.fecha ? ` · Cobrada: ${fmtDate(cobro.fecha)}` : ''}
                              {!esAbono && f.estado === 'emitida' && fechaVencimiento(f.fecha) ? ` · Vence: ${fmtDate(fechaVencimiento(f.fecha))}` : ''}
                            </p>
                            {f.abonoEmitido && (
                              <p className="text-[10px] text-purple-600 font-semibold mt-0.5">↩ Abono emitido: {f.abonoRef || ''}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`text-sm font-bold ${esAbono ? 'text-purple-700' : 'text-gray-900'}`}>
                              {esAbono ? '-' : ''}{fmt(importe)}
                            </p>
                            {cobro?.reciboRef && (
                              <p className="text-[10px] text-emerald-600 font-semibold">{cobro.reciboRef}</p>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Quick cobrar + sugerencia de conciliación (solo pendiente) */}
                      {tab === 'pendiente' && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {isQuick ? (
                            <QuickCobrarForm
                              factura={f}
                              onSave={updated => {
                                setQuickCobrarId(null);
                                // Ya no hay onSnapshot — se parchea en local.
                                setFacturas(prev => prev.map(x => x.id === updated.id ? updated : x));
                              }}
                              onClose={() => setQuickCobrarId(null)}
                            />
                          ) : (
                            <>
                              <button
                                onClick={e => { e.stopPropagation(); setQuickCobrarId(f.id); }}
                                className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 transition-colors"
                              >
                                ✓ Registrar cobro rápido
                              </button>
                              <SugerenciaConciliacion
                                factura={f}
                                movimientos={movsSinConciliar}
                                conciliando={conciliandoId === f.id}
                                onConciliar={mov => handleConciliarDesdeFacturas(f, mov)}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!loading && listaFiltrada.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-50">
                <p className="text-xs text-gray-400">
                  Mostrando {desde}–{hasta} de {listaFiltrada.length}
                </p>
                <Paginacion page={paginaReal} totalPages={totalPaginas} onChange={setPagina} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Panel detalle */}
      {seleccionada && (
        <FacturaPanel
          factura={seleccionada}
          onClose={() => setSeleccionada(null)}
          onUpdated={updated => {
            setSeleccionada(updated);
            // Ya no hay onSnapshot — se parchea la lista en local.
            setFacturas(prev => prev.map(x => x.id === updated.id ? updated : x));
          }}
        />
      )}
    </div>
  );
}
